import { useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Alert, Button, Space, Typography, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { DOOH_ACCEPT, DOOH_MAX_IMAGE_BYTES, DOOH_MAX_VIDEO_BYTES, validateDoohFile } from '../utils/media';
import type { DoohFileValidation } from '../utils/media';
import { MEDIA_KIND_LABELS, formatBytes } from '../utils/format';
import { MediaKindBadge } from './DoohBadges';

export interface MediaUploadFormProps {
  /** Sube el archivo validado; la página maneja éxito/error y cierra el modal. */
  onUpload: (file: File) => Promise<void>;
}

/**
 * Subida de un medio JPG/MP4 (TG-58). Valida tipo/tamaño en cliente antes de enviar
 * (el backend revalida) y solo habilita la subida con un archivo válido. La selección
 * NO autosube (`beforeUpload` devuelve `false`): el operador confirma con el botón.
 */
export function MediaUploadForm({ onUpload }: MediaUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<DoohFileValidation | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const beforeUpload: UploadProps['beforeUpload'] = (candidate) => {
    const typed = candidate as File;
    setFile(typed);
    setValidation(validateDoohFile(typed));
    return false;
  };

  const reset = () => {
    setFile(null);
    setValidation(null);
  };

  const submit = async () => {
    if (!file || !validation?.valid) {
      return;
    }
    setSubmitting(true);
    try {
      await onUpload(file);
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Upload.Dragger
        name="dooh-media-file"
        accept={DOOH_ACCEPT}
        maxCount={1}
        showUploadList={false}
        beforeUpload={beforeUpload}
        onRemove={reset}
        aria-label="Seleccionar archivo JPG o MP4"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Haz clic o arrastra un JPG/MP4 aquí</p>
        <p className="ant-upload-hint">
          JPG hasta {formatBytes(DOOH_MAX_IMAGE_BYTES)} · MP4 hasta{' '}
          {formatBytes(DOOH_MAX_VIDEO_BYTES)}
        </p>
      </Upload.Dragger>

      {file && (
        <Space wrap>
          {validation?.valid ? (
            <MediaKindBadge kind={validation.kind} />
          ) : null}
          <Typography.Text>{file.name}</Typography.Text>
          <Typography.Text type="secondary">{formatBytes(file.size)}</Typography.Text>
        </Space>
      )}

      {file && validation && !validation.valid && (
        <Alert type="error" showIcon message={validation.error} />
      )}

      <Space>
        <Button onClick={reset} disabled={!file || submitting}>
          Quitar
        </Button>
        <Button
          type="primary"
          onClick={() => void submit()}
          loading={submitting}
          disabled={!file || !validation?.valid}
        >
          Subir a la biblioteca
        </Button>
      </Space>

      {validation?.valid && (
        <Typography.Text type="secondary">
          Se guardará como {MEDIA_KIND_LABELS[validation.kind].toLowerCase()} en la biblioteca.
        </Typography.Text>
      )}
    </Space>
  );
}
